import { FunctionComponent, JSX, SyntheticEvent, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import styles from './addFamilyDialog.module.scss'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Email, QrCodeScanner } from '@mui/icons-material'
import { useMutation } from '@apollo/client'
import { GET_AUTO_LINK_MAIL } from '@/modules/API/software/autoLinkHandler'
import ShowQRCodeDialog from './showQRCodeDialog'
import UserInput from '@/components/_custom/UserInput/userInput'
import { ADD_PARENT } from '@/modules/API/parent/parentAssociationHandler'
import { CREATE_MOCK_CHILD } from '@/modules/API/parent/noAccountHandler'
import Tabs from '@/components/_custom/Tabs/tabs'
import { useAuth } from '@/modules/context/AuthContext'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import DatePickerComponent from '@/components/_custom/Picker/datePicker/datePicker'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { Daycare } from '@/types/Daycare.ts'

interface AddFamilyDialogProps {
    open: boolean
    closeDialog: () => void
    daycare?: Daycare
    isRpeOrLaep: boolean
    autoLinkActive: boolean
    autoLinkDaycare: string
    refetchAllParent: () => void
    refetchAllChilds: () => void
}

const AddFamilyDialog: FunctionComponent<AddFamilyDialogProps> = ({
    open,
    closeDialog,
    daycare,
    isRpeOrLaep,
    autoLinkActive,
    autoLinkDaycare,
    refetchAllParent,
    refetchAllChilds
}: AddFamilyDialogProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const navigate: NavigateFunction = useNavigate()

    const [tabSwitch, setTabSwitch] = useState<number>(0)
    const [mailSent, setMailSent] = useState<boolean>(false)

    const [email, setEmail] = useState<string>('')
    const [validEmail, setValidEmail] = useState<boolean>(false)

    const [firstname, setFirstname] = useState<string>('')
    const [birthdate, setBirthdate] = useState<string>('')
    const [preRegistrationBelgium, setPreRegistrationBelgium] = useState<string>('')

    const [isQRCodeDialogOpen, setQRCodeDialogOpen] = useState<boolean>(false)

    const [sendAutoLinkMail] = useMutation(GET_AUTO_LINK_MAIL, {
        variables: {
            daycareId: Number(daycare?.id || 0),
            inviteParent: !isRpeOrLaep
        },
        onCompleted: () => {
            setMailSent(true)

            showNotificationSuccess(t('autoLinkMailSuccess'))
        },
        onError: () => {
            showNotificationError(t('autoLinkMailError'))
        }
    })

    const [addParent, { loading: loadingAddParent }] = useMutation(ADD_PARENT, {
        variables: {
            daycare_id: Number(daycare?.id || 0),
            email_parent: email
        },
        onCompleted: () => {
            showNotificationSuccess(t(isRpeOrLaep ? 'addPublicSuccess' : 'addFamilySuccess'))

            refetchAllParent()
            refetchAllChilds()

            closeDialog()
        },
        onError: () => {
            showNotificationError(t(isRpeOrLaep ? 'addPublicError' : 'addFamilyError'))
        }
    })

    const [createChild, { loading: loadingCreateChild }] = useMutation(CREATE_MOCK_CHILD, {
        variables: {
            daycareId: Number(daycare?.id || 0),
            firstname: firstname,
            birthdate: birthdate,
            preRegistrationBelgium: preRegistrationBelgium
        },
        onCompleted: () => {
            showNotificationSuccess(t('childCreated'))

            refetchAllParent()
            refetchAllChilds()

            closeDialog()
        },
        onError: () => {
            showNotificationError(t('errorCreatingChild'))
        }
    })

    function handleSwitch(_e: SyntheticEvent, newValue: number): void {
        setTabSwitch(newValue)
    }

    const autoLinkPrefix: string = (import.meta.env.VITE_PUBLIC_URL || 'https://app.cald.fr') + '/?autoLink='

    const daycareIsInBelgium: boolean = daycare?.country === 'BE'

    function renderInviteTab(): JSX.Element {
        return (
            <div className={styles.InviteContainer}>
                {!autoLinkActive && (
                    <p className={styles.Text}>
                        {t('autoLinkNotActivated')}
                        <a
                            onClick={() => navigate(`/daycare?daycare=${daycare?.id || 0}#autolink`)}
                            className={styles.Clickable}
                        >
                            {t('clickHere')}
                        </a>
                    </p>
                )}
                {mailSent && <p className={styles.Text}>{t('autoLinkMailReceived')}</p>}
                <div className={styles.ButtonInviteContainer}>
                    <ButtonTextIcon type={'close'} onClick={closeDialog} className={styles.Button} />
                    <ButtonTextIcon
                        type={'custom'}
                        onClick={() => setQRCodeDialogOpen(true)}
                        customText={t('showQRCode')}
                        disabled={!autoLinkActive}
                        customIcon={<QrCodeScanner />}
                        className={styles.Button}
                    />
                    {user?.currentDaycareRights.canReadParamsDaycare && (
                        <ButtonTextIcon
                            type={'custom'}
                            onClick={sendAutoLinkMail}
                            customText={t('receiveMail')}
                            disabled={!autoLinkActive || mailSent}
                            customIcon={<Email />}
                            className={styles.Button}
                        />
                    )}
                </div>
                <ShowQRCodeDialog
                    open={isQRCodeDialogOpen}
                    onClose={() => setQRCodeDialogOpen(false)}
                    pdfVariant={isRpeOrLaep ? 'rpe' : 'eaje'}
                    autoLink={`${autoLinkPrefix}${autoLinkDaycare}${isRpeOrLaep ? '&role=assmat' : '&role=parent'}`}
                    daycareName={daycare?.name}
                    daycareLogoBase64Data={daycare?.logoBase64Data}
                />
            </div>
        )
    }

    function renderAddTab(): JSX.Element {
        return (
            <div className={styles.InviteContainer}>
                <div className={styles.Text}>
                    {t('RgpdInviteFamily')}
                    <a onClick={(e) => handleSwitch(e, 0)} className={styles.Clickable}>
                        {t('clickHere')}
                    </a>
                </div>
                <div className={styles.InputContainer}>
                    <UserInput
                        label={t('email')}
                        value={email}
                        setValue={setEmail}
                        fullWidth
                        verify={'email'}
                        unallowedCharacters={'email'}
                        setValid={setValidEmail}
                        className={styles.Input}
                    />
                </div>
                <div className={styles.ButtonAddContainer}>
                    <ButtonTextIcon type={'close'} onClick={closeDialog} className={styles.Button} />
                    <ButtonTextIcon
                        type={'add'}
                        onClick={addParent}
                        className={styles.Button}
                        disabled={!validEmail || loadingAddParent}
                    />
                </div>
            </div>
        )
    }

    function renderCreateTab(): JSX.Element {
        return (
            <div className={styles.InviteContainer}>
                <div className={styles.Text}>
                    {`${t('NoAccount1Child')} ${t('NoAccount2Child')} ${t('NoAccount3')}`}
                    <a onClick={(e) => handleSwitch(e, 0)} className={styles.Clickable}>
                        {t('clickHere')}
                    </a>
                </div>
                <div className={styles.InputContainer}>
                    <UserInput
                        label={t('firstname')}
                        value={firstname}
                        fullWidth
                        required
                        className={styles.Input}
                        setValue={setFirstname}
                        format={'pascal'}
                        type={'text'}
                    />
                </div>
                <div className={styles.InputContainer}>
                    <DatePickerComponent
                        label={t('birthdate')}
                        value={birthdate}
                        setValue={setBirthdate}
                        disablePast={false}
                        className={styles.Input}
                        allowEmpty
                    />
                </div>
                {daycareIsInBelgium && (
                    <div className={styles.InputContainer}>
                        <UserInput
                            label={t('preRegistration')}
                            value={preRegistrationBelgium}
                            fullWidth
                            className={styles.Input}
                            setValue={setPreRegistrationBelgium}
                            format={'pascal'}
                            type={'text'}
                        />
                    </div>
                )}
                <div className={styles.ButtonAddContainer}>
                    <ButtonTextIcon type={'close'} onClick={closeDialog} className={styles.Button} />
                    <ButtonTextIcon
                        type={'create'}
                        onClick={createChild}
                        className={styles.Button}
                        disabled={loadingCreateChild || firstname == '' || birthdate == ''}
                    />
                </div>
            </div>
        )
    }

    function renderTabContent(): JSX.Element | null {
        switch (tabSwitch) {
            case 0:
                return renderInviteTab()
            case 1:
                return user?.currentDaycareRights.canWriteFamilies ? renderAddTab() : null
            case 2:
                return user?.currentDaycareRights.canWriteFamilies ? renderCreateTab() : null
            default:
                return null
        }
    }

    return (
        <Dialog open={open} handleClose={closeDialog}>
            <DialogHeader title={defineDialogTitle(tabSwitch, isRpeOrLaep, t)} />
            <DialogBody className={styles.DialogBody}>
                <Tabs
                    value={tabSwitch}
                    onChange={handleSwitch}
                    tabList={
                        user?.currentDaycareRights.canWriteFamilies
                            ? [t('invite'), t('add'), t('create')]
                            : [t('invite')]
                    }
                />
                {renderTabContent()}
            </DialogBody>
        </Dialog>
    )
}

export default AddFamilyDialog

function defineDialogTitle(tab: number, isRpeOrLaep: boolean, t: (v: string) => string): string {
    if (tab === 0) {
        return isRpeOrLaep ? t('invitePublic') : t('inviteFamily')
    } else if (tab === 1) {
        return isRpeOrLaep ? t('addPublic') : t('addFamily')
    }

    return t('createChild')
}
